import { keepPreviousData, skipToken, useQuery } from '@tanstack/react-query';
import { hydraUiApi } from '@/services/api';
import { isEmptyString } from '@/utils/string';

export const eventNameKeys = {
  all: ['event-names'] as const,
  pickerSearch: (workspaceId: number, term: string) => ['event-names', workspaceId, 'picker-search', term] as const,
  // Sentinel key for the picker search's disabled (empty-term) state.
  pickerSearchInactive: (workspaceId: number) => ['event-names', workspaceId, 'picker-search', 'inactive'] as const,
};

/**
 * Case-preserved event names matching a search term, for condition pickers.
 *
 * Maps `metadata.last_seen_as` and drops results without it, NOT the list
 * item's `id`: the backend stores ids lowercased, and only `last_seen_as`
 * preserves the casing the customer's events were ingested with. Event-name
 * matching in conditions is byte-exact, so a picker showing a lowercased name
 * would silently match nothing. Coverage of legacy events without
 * `last_seen_as` is traded for correctness. (The Activity Log's name search
 * maps ids instead — its filter is a server-side substring match, where
 * casing doesn't bite.)
 *
 * @public
 */
export async function searchEventConditionNames(workspaceId: number, term: string): Promise<string[]> {
  const raw = await hydraUiApi.events.getEventNames({
    params: { path: { environment_id: String(workspaceId) }, query: { search: term, page: 1, size: 50 } },
  });
  const names: string[] = [];
  for (const event of raw.events ?? []) {
    const lastSeenAs = event.metadata?.last_seen_as;
    if (lastSeenAs != null && lastSeenAs !== '') {
      names.push(lastSeenAs);
    }
  }
  return names;
}

/**
 * Debounce-friendly picker search: only fires while there is a term, and the
 * previous matches stay visible while a narrower term loads.
 *
 * @public
 */
export function useEventConditionNameSearch(workspaceId: number, term: string) {
  const trimmed = term.trim();
  const active = !isEmptyString(trimmed);
  return useQuery({
    queryKey: active
      ? eventNameKeys.pickerSearch(workspaceId, trimmed)
      : eventNameKeys.pickerSearchInactive(workspaceId),
    queryFn: active ? () => searchEventConditionNames(workspaceId, trimmed) : skipToken,
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  });
}
